/**
 * Which care-mode actions are gated, and what happens when no PIN exists.
 *
 * Non-negotiable §3.10: **only destructive actions are PIN-gated.** Adding a phrase must never
 * require a PIN — the person has to be able to capture a word mid-conversation without waking
 * someone at 3 a.m., and a gate on creation would make care mode something you avoid rather than
 * something you use.
 *
 * Pure and enumerated so the rule is testable rather than a habit each screen has to remember.
 */

export const DESTRUCTIVE_ACTIONS = [
  'deletePhrase',
  'deleteCategory',
  'deleteAbbrev',
  'resetSettings',
  'wipeData',
  'importBackup',
] as const;

export const OPEN_ACTIONS = [
  'addPhrase',
  'editPhrase',
  // Arranging is not destruction: a move changes where a tile is, never whether it exists (`AUT-02`).
  'movePhrase',
  'addCategory',
  'editCategory',
  'moveCategory',
  'addAbbrev',
  'editAbbrev',
  'editSettings',
  'runCalibration',
  'acceptProposal',
  'prerenderAudio',
  'exportBackup',
  'installUpdate',
  'setPin',
] as const;

export type CareAction = (typeof DESTRUCTIVE_ACTIONS)[number] | (typeof OPEN_ACTIONS)[number];

export function isDestructive(action: CareAction): boolean {
  return (DESTRUCTIVE_ACTIONS as readonly string[]).includes(action);
}

/**
 * `importBackup` is in the destructive list on purpose: it replaces the whole database, so it can
 * discard a vocabulary the person has been building. It reads like a safe, restorative action and
 * is the most dangerous button in care mode.
 */

export type GateResult =
  /** Proceed immediately. */
  | { kind: 'allow' }
  /** Ask for the PIN. */
  | { kind: 'pin' }
  /** No PIN is set, so a plain confirmation stands in for one. */
  | { kind: 'confirm' };

/**
 * With no PIN set, destructive actions take a confirmation step rather than being blocked.
 *
 * Blocking would mean a caregiver who wants to remove one phrase must first invent and remember a
 * PIN, and being told no for a reason you did not ask about is how a tool starts feeling like an
 * obstacle. Protection is opt-in; care mode prompts for a PIN prominently instead.
 */
export function gateFor(action: CareAction, pin: string | null): GateResult {
  if (!isDestructive(action)) return { kind: 'allow' };
  return pin === null ? { kind: 'confirm' } : { kind: 'pin' };
}

/** Constant-time-ish comparison is pointless here; the threat is a mis-tap, not an attacker. */
export function pinMatches(entered: string, pin: string | null): boolean {
  if (pin === null) return true;
  return entered === pin;
}

export const PIN_LENGTH = 4;

export function isValidNewPin(pin: string): boolean {
  return new RegExp(`^\\d{${PIN_LENGTH}}$`).test(pin);
}
